import { useState, useMemo, useRef, useEffect } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { X, Loader2, Check, ChevronDown } from 'lucide-react';
import { generateOrgAsset, enhanceImagePrompt } from '../lib/api';
import { useBackgroundTasksStore, TASK_TYPES, useOrgAssetsStore, useEditorUIStore } from '../stores';
import PromptBox from './editor/PromptBox';
import ModelSelector from './editor/ModelSelector';
import { IMAGE_MODEL_OPTIONS_GENERATE } from '../lib/imageModels';
import { ASPECT_RATIO_OPTIONS } from '../lib/aspectRatioOptions';
import { getAssetMediaType } from '../utils/assetImageResolver';
import './editor/canvas-modes/CreatingAssetView.css';

const DEFAULT_ASSET_ASPECT_RATIO = '1:1';
const ASPECT_RATIO_SWATCH_MAX_SIZE = 18;
const ASPECT_RATIO_SWATCH_MIN_SIZE = 8;

function getAspectRatioSwatchStyle(value) {
  const [rawWidth, rawHeight] = String(value || DEFAULT_ASSET_ASPECT_RATIO)
    .split(':')
    .map((part) => Number(part));

  if (!rawWidth || !rawHeight) {
    return { width: ASPECT_RATIO_SWATCH_MAX_SIZE, height: ASPECT_RATIO_SWATCH_MAX_SIZE };
  }

  if (rawWidth >= rawHeight) {
    return {
      width: ASPECT_RATIO_SWATCH_MAX_SIZE,
      height: Math.max(
        ASPECT_RATIO_SWATCH_MIN_SIZE,
        Math.round((ASPECT_RATIO_SWATCH_MAX_SIZE * rawHeight) / rawWidth)
      ),
    };
  }

  return {
    width: Math.max(
      ASPECT_RATIO_SWATCH_MIN_SIZE,
      Math.round((ASPECT_RATIO_SWATCH_MAX_SIZE * rawWidth) / rawHeight)
    ),
    height: ASPECT_RATIO_SWATCH_MAX_SIZE,
  };
}

function AssetAspectRatioPicker({ value, onChange, disabled, options }) {
  const [open, setOpen] = useState(false);
  const selected = options.find((option) => option.value === value) || options[0];

  return (
    <div className="asset-aspect-ratio-picker">
      <DropdownMenu.Root open={open} onOpenChange={setOpen} modal={false}>
        <DropdownMenu.Trigger asChild>
          <button
            type="button"
            className="asset-aspect-ratio-trigger"
            aria-label="Aspect ratio"
            aria-haspopup="menu"
            aria-expanded={open}
            disabled={disabled}
            title="Aspect ratio"
          >
            <span
              className="asset-aspect-ratio-swatch"
              style={getAspectRatioSwatchStyle(selected?.value)}
              aria-hidden="true"
            />
            <span className="asset-aspect-ratio-value">{selected?.label || value}</span>
            <ChevronDown size={14} className={`asset-aspect-ratio-icon ${open ? 'asset-aspect-ratio-icon-open' : ''}`} />
          </button>
        </DropdownMenu.Trigger>

        <DropdownMenu.Portal>
          <DropdownMenu.Content className="asset-aspect-ratio-content" sideOffset={8} align="end">
            {options.map((option) => (
              <DropdownMenu.Item
                key={option.value}
                className="asset-aspect-ratio-item"
                onSelect={() => onChange(option.value)}
                disabled={disabled}
              >
                <span
                  className="asset-aspect-ratio-item-swatch"
                  style={getAspectRatioSwatchStyle(option.value)}
                  aria-hidden="true"
                />
                <span className="asset-aspect-ratio-item-copy">
                  <span className="asset-aspect-ratio-item-label">{option.label}</span>
                  <span className="asset-aspect-ratio-item-description">{option.description}</span>
                </span>
                {value === option.value && <Check size={14} className="asset-aspect-ratio-check" />}
              </DropdownMenu.Item>
            ))}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </div>
  );
}

/**
 * Reusable component for creating org-level assets (and sub-assets).
 * Mirrors CreatingAssetView.js but calls org-asset endpoints.
 *
 * Props:
 *   onAssetGenerated(asset) - called on success
 *   onCancel()              - called when user closes/cancels
 *   parentAssetId           - if set, creates a sub-asset under this parent
 *   mode                    - 'generate' | 'upload' (default 'generate')
 */
function OrgAssetCreateView({ onAssetGenerated, onCancel, parentAssetId = null, parentAsset = null }) {
  const [prompt, setPrompt] = useState('');
  const [aspectRatio, setAspectRatio] = useState(DEFAULT_ASSET_ASPECT_RATIO);
  const [uploadedImages, setUploadedImages] = useState(() => {
    if (parentAsset?.imageUrl) {
      return [{
        id: `parent-asset-${parentAsset.id}`,
        name: parentAsset.name,
        preview: parentAsset.imageUrl,
        imageUrl: parentAsset.imageUrl,
        isAssetMention: true,
      }];
    }
    return [];
  });
  const [isGenerating, setIsGenerating] = useState(false);
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [error, setError] = useState(null);
  const [contentPolicyError, setContentPolicyError] = useState(null);

  const imageModel = useEditorUIStore((state) => state.imageGenerationModel);
  const setImageModel = useEditorUIStore((state) => state.setImageGenerationModel);

  const addTask = useBackgroundTasksStore((state) => state.addTask);
  const completeTask = useBackgroundTasksStore((state) => state.completeTask);
  const failTask = useBackgroundTasksStore((state) => state.failTask);
  const markTaskAsBackground = useBackgroundTasksStore((state) => state.markTaskAsBackground);

  const addOrgAsset = useOrgAssetsStore((state) => state.addOrgAsset);
  const orgAssets = useOrgAssetsStore((state) => state.orgAssets);

  const isMountedRef = useRef(true);
  const currentTaskIdRef = useRef(null);

  const aspectRatioOptions = useMemo(() => {
    if (imageModel === 'reve') {
      return ASPECT_RATIO_OPTIONS.filter((option) => option.value !== '21:9');
    }
    return ASPECT_RATIO_OPTIONS;
  }, [imageModel]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (!aspectRatioOptions.some((option) => option.value === aspectRatio)) {
      setAspectRatio(DEFAULT_ASSET_ASPECT_RATIO);
    }
  }, [aspectRatio, aspectRatioOptions]);

  // @mention items from existing top-level org assets (for reference)
  const mentionableItems = useMemo(() =>
    orgAssets
      .filter((a) => !a.parentAssetId && getAssetMediaType(a) === 'image' && a.imageUrl)
      .map((a) => ({
        id: a.id,
        name: a.name,
        imageUrl: a.imageUrl,
        type: 'org-asset',
        displayName: a.name,
      })),
  [orgAssets]);

  const handleEnhancePrompt = async () => {
    if (!prompt.trim() || isEnhancing || isGenerating) return;
    try {
      setIsEnhancing(true);
      setError(null);
      const response = await enhanceImagePrompt({ text: prompt.trim() });
      if (response?.enhanced_prompt) setPrompt(response.enhanced_prompt);
    } catch (err) {
      setError('Failed to enhance prompt.');
    } finally {
      setIsEnhancing(false);
    }
  };

  const runGenerate = async (promptText, taskId) => {
    const imageFiles = uploadedImages.filter((img) => img.file).map((img) => img.file);
    const referenceUrls = uploadedImages
      .filter((img) => img.isAssetMention && img.imageUrl)
      .map((img) => img.imageUrl);

    const response = await generateOrgAsset(
      promptText,
      imageFiles,
      null,
      {
        model: imageModel,
        aspectRatio,
        parentAssetId: parentAssetId || undefined,
        referenceImageUrls: referenceUrls.length > 0 ? referenceUrls : undefined,
      }
    );

    if (response.success && response.asset) {
      completeTask(taskId, {
        assetId: response.asset.id,
        assetUrl: response.asset.imageUrl,
        assetName: response.asset.name,
      });
      if (isMountedRef.current) {
        addOrgAsset(response.asset);
        setIsGenerating(false);
        setPrompt('');
        setUploadedImages([]);
        if (onAssetGenerated) onAssetGenerated(response.asset);
      }
    } else {
      throw new Error('Invalid response from server');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!prompt.trim() && uploadedImages.length === 0) return;

    setIsGenerating(true);
    setError(null);

    const taskId = `org-asset-create-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
    currentTaskIdRef.current = taskId;
    addTask({
      id: taskId,
      type: TASK_TYPES.ORG_ASSET_CREATE,
      prompt: prompt.trim(),
      aspectRatio,
      createdAt: Date.now(),
    });

    try {
      await runGenerate(prompt, taskId);
    } catch (err) {
      console.error('[OrgAssetCreateView] generation failed:', err);
      failTask(taskId, err);
      if (isMountedRef.current) {
        if (err.code === 'CONTENT_POLICY_VIOLATION') {
          setContentPolicyError({ originalPrompt: err.originalPrompt || prompt });
          setError(null);
        } else {
          setError('Failed to generate asset. Please try again.');
          setContentPolicyError(null);
        }
        setIsGenerating(false);
      }
    }
  };

  const handleContentPolicyRetry = async (editedPrompt) => {
    setContentPolicyError(null);
    setPrompt(editedPrompt);
    setIsGenerating(true);
    setError(null);

    const taskId = `org-asset-create-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
    currentTaskIdRef.current = taskId;
    addTask({ id: taskId, type: TASK_TYPES.ORG_ASSET_CREATE, prompt: editedPrompt.trim(), aspectRatio, createdAt: Date.now() });

    try {
      await runGenerate(editedPrompt, taskId);
    } catch (err) {
      failTask(taskId, err);
      if (isMountedRef.current) {
        if (err.code === 'CONTENT_POLICY_VIOLATION') {
          setContentPolicyError({ originalPrompt: err.originalPrompt || editedPrompt });
        } else {
          setError('Failed to generate asset. Please try again.');
        }
        setIsGenerating(false);
      }
    }
  };

  return (
    <div className="creating-asset-view">
      {onCancel && (
        <button
          onClick={() => {
            if (isGenerating && currentTaskIdRef.current) {
              markTaskAsBackground(currentTaskIdRef.current);
            }
            onCancel();
          }}
          className="creating-asset-close-btn"
          aria-label={isGenerating ? 'Continue in background' : 'Close'}
          title={isGenerating ? 'Generation will continue in background' : undefined}
        >
          <X size={18} />
        </button>
      )}

      <div className="creating-asset-content">
        {!isGenerating && (
          <div className="creating-asset-header">
            <span className="creating-asset-label">
              {parentAssetId ? 'Add Sub-Asset' : 'Create Asset'}
            </span>
            <span className="creating-asset-hint">
              {parentAssetId
                ? 'Generate a variation or alternate view'
                : 'Generate a reusable asset for your organization'}
            </span>
          </div>
        )}

        {isGenerating && (
          <div className="creating-asset-generating">
            <Loader2 size={24} className="creating-asset-spinner" />
            <span className="creating-asset-generating-text">Generating…</span>
          </div>
        )}

        {!isGenerating && (
          <PromptBox
            value={prompt}
            onChange={setPrompt}
            onSubmit={handleSubmit}
            isLoading={isGenerating}
            label="Asset description"
            placeholder="Describe the asset you want to generate…"
            uploadedImages={uploadedImages}
            setUploadedImages={setUploadedImages}
            mentionableItems={mentionableItems}
            error={error}
            contentPolicyError={contentPolicyError}
            onContentPolicyRetry={handleContentPolicyRetry}
            onContentPolicyDismiss={() => setContentPolicyError(null)}
            additionalActions={
              <>
                <AssetAspectRatioPicker
                  value={aspectRatio}
                  onChange={setAspectRatio}
                  disabled={isGenerating || isEnhancing}
                  options={aspectRatioOptions}
                />
                <button
                  type="button"
                  className="prompt-box-text-button"
                  disabled={isGenerating || isEnhancing || !prompt.trim()}
                  title="Enhance prompt"
                  onClick={handleEnhancePrompt}
                >
                  {isEnhancing ? 'Enhancing…' : 'Enhance'}
                </button>
                <ModelSelector
                  value={imageModel}
                  onChange={setImageModel}
                  disabled={isGenerating || isEnhancing}
                  variant="compact"
                  options={IMAGE_MODEL_OPTIONS_GENERATE}
                  theme="neutral"
                />
              </>
            }
          />
        )}
      </div>
    </div>
  );
}

export default OrgAssetCreateView;
